import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Heading, Text, useDisclosure } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { VideoModal } from '~/components/video-modal';
import { trackEvent } from '~/features/analytics/track-event';
import { GenericPersonAvatar } from '~/features/enterprise/components/generic-person-avatar';
import { BriefcaseIcon, ChatCircleIcon, PlayIcon, StethoscopeIcon } from '~/features/enterprise/landing-pages/icons';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';

export type SkillsChallengeConfig = {
  video?: {
    videoId: string;
    thumbnail: { src: string };
  };
  /** Portrait image rendered inside the dark card when `!showQuotePanel`. */
  image?: { alt?: string; iconId?: 'generic-person'; src?: string };
  /** Optional CTA link rendered below the attribution when `!showQuotePanel`. */
  cta?: { href: string };
  /**
   * When false, the light-blue quote panel below the dark card is suppressed
   * and the quote is rendered inside the dark card instead.
   * Defaults to true (existing Skills-page behaviour is unchanged).
   */
  quotePanel?: boolean;
  /** Icon shown in the section header. Defaults to 'stethoscope'. */
  headerIconId?: 'chat-circle' | 'stethoscope';
};

export function SkillsChallengeSection({ config, slug }: { config: SkillsChallengeConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.skillsChallenge`;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const showQuotePanel = config.quotePanel !== false;
  const showGenericPersonIcon = config.image?.iconId === 'generic-person';

  function renderTestimonialImage(alt: string, sizes: string) {
    if (showGenericPersonIcon) {
      return <GenericPersonAvatar iconSize='120px' variant='dark' />;
    }

    if (!config.image?.src) {
      return null;
    }

    return (
      <Image
        alt={alt}
        loading='lazy'
        quality={85}
        sizes={sizes}
        src={config.image.src}
        style={{ objectFit: 'cover' }}
        fill
      />
    );
  }

  const descriptionLines = config.video
    ? (t(`${tPrefix}.video.descriptionLines`, { returnObjects: true }) as string[])
    : [];

  function handlePlayVideo() {
    if (!config.video) return;
    trackEvent({
      name: 'Enterprise Platform CTA Clicked',
      type: 'click',
      label: analyticsLabels.challengeVideo,
    });
    onOpen();
  }

  return (
    <>
      <Box
        as='section'
        pb={{
          base: '40px',
          md: '64px',
        }}
      >
        <ContentContainer
          contentMaxWidth='content-max-width-md'
          display='flex'
          flexDirection='column'
          gap={{ base: '24px', md: '40px' }}
        >
          <SectionHeader
            color='grey.500'
            subtitle={t(`${tPrefix}.subtitle`) as string}
            descriptionSlot={
              <Text color='grey.500' margin='0 auto' maxWidth='843px'>
                {t(`${tPrefix}.description`)}
              </Text>
            }
            iconSlot={
              config.headerIconId === 'chat-circle' ? (
                <ChatCircleIcon color='grey.500' height='16px' width='16px' />
              ) : (
                <StethoscopeIcon color='grey.500' height='16px' width='16px' />
              )
            }
            titleSlot={
              <Heading
                as='h2'
                color='grey.900'
                fontSize={{ base: '2.25rem', md: '3.25rem' }}
                fontWeight='600'
                letterSpacing='-2px'
                lineHeight='1.2'
              >
                {t(`${tPrefix}.title`)}
              </Heading>
            }
          />

          <Flex direction='column' gap='16px'>
            <Box
              bgGradient='linear(to-r, #002989, #0a0c11)'
              height={{ base: 'auto', md: '508px' }}
              overflow='hidden'
              position='relative'
              borderRadius={{
                md: '8px',
              }}
              mx={{
                base: '-16px',
                md: 'unset',
              }}
              padding={{
                base: '24px 16px',
                md: '75px 72px',
              }}
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                height='100%'
                position='relative'
                gap={{
                  base: 6,
                  md: 12,
                }}
              >
                {config.video && (
                  <Flex
                    direction='column'
                    gap={12}
                    justifyContent='center'
                    position='relative'
                    flex={{
                      md: '1 2 380px',
                    }}
                  >
                    <Flex color='white' direction='column' gap='18px'>
                      <Heading
                        as='h3'
                        fontSize={{ base: '1.75rem', md: '2.25rem' }}
                        fontWeight='600'
                        letterSpacing='-1px'
                        lineHeight='1.25'
                      >
                        {t(`${tPrefix}.video.title`)}
                      </Heading>
                      <Box fontSize='18px' lineHeight='1.4'>
                        {descriptionLines.map((line) => (
                          <Text key={line}>{line}</Text>
                        ))}
                      </Box>
                    </Flex>
                    <Button
                      borderWidth='2px'
                      color='white'
                      colorScheme='white'
                      fontSize='15px'
                      fontWeight='500'
                      gap='8px'
                      variant='outline'
                      width={{
                        md: 'fit-content',
                      }}
                      onClick={handlePlayVideo}
                    >
                      <PlayIcon height='20px' width='20px' />
                      {t(`${tPrefix}.video.ctaLabel`)}
                    </Button>
                  </Flex>
                )}

                {config.video && (
                  <Flex
                    alignItems='center'
                    display={{ base: 'none', md: 'flex' }}
                    justifyContent='center'
                    flex={{
                      md: '1 1 490px',
                    }}
                  >
                    <Box
                      aria-label='Play video'
                      borderRadius='7px'
                      cursor='pointer'
                      height='100%'
                      overflow='hidden'
                      position='relative'
                      role='button'
                      tabIndex={0}
                      width='100%'
                      onClick={handlePlayVideo}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handlePlayVideo();
                      }}
                    >
                      <Image
                        alt={t(`${tPrefix}.video.thumbnailAlt`)}
                        loading='lazy'
                        quality={85}
                        sizes='(max-width: 768px) 100vw, 621px'
                        src={config.video.thumbnail.src}
                        style={{ objectFit: 'cover' }}
                        fill
                      />
                      <Flex
                        alignItems='center'
                        aria-hidden='true'
                        inset={0}
                        justifyContent='center'
                        position='absolute'
                      >
                        <Flex
                          alignItems='center'
                          bg='blue.500'
                          borderRadius='full'
                          color='white'
                          height='72px'
                          justifyContent='center'
                          width='72px'
                        >
                          <PlayIcon boxSize='32px' />
                        </Flex>
                      </Flex>
                    </Box>
                  </Flex>
                )}

                {config.video && (
                  <Box
                    aria-label='Play video'
                    borderRadius='7px'
                    cursor='pointer'
                    display={{ base: 'block', md: 'none' }}
                    height='240px'
                    overflow='hidden'
                    position='relative'
                    role='button'
                    tabIndex={0}
                    onClick={handlePlayVideo}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handlePlayVideo();
                    }}
                  >
                    <Image
                      alt={t(`${tPrefix}.video.thumbnailAlt`)}
                      loading='lazy'
                      quality={85}
                      sizes='100vw'
                      src={config.video.thumbnail.src}
                      style={{ objectFit: 'cover' }}
                      fill
                    />
                    <Flex alignItems='center' aria-hidden='true' inset={0} justifyContent='center' position='absolute'>
                      <Flex
                        alignItems='center'
                        bg='blue.500'
                        borderRadius='full'
                        color='white'
                        height='56px'
                        justifyContent='center'
                        width='56px'
                      >
                        <PlayIcon boxSize='24px' />
                      </Flex>
                    </Flex>
                  </Box>
                )}

                {!showQuotePanel && config.image && (
                  <Box
                    borderRadius='7px'
                    display={{ base: 'block', md: 'none' }}
                    height='240px'
                    overflow='hidden'
                    position='relative'
                  >
                    {renderTestimonialImage(config.image.alt ?? '', '100vw')}
                  </Box>
                )}

                {!showQuotePanel && (
                  <Flex
                    align={config.image ? 'flex-start' : 'center'}
                    as='blockquote'
                    direction='column'
                    flex={config.image ? { md: '1 1 292px' } : undefined}
                    gap='32px'
                    justify='center'
                    px={config.image ? undefined : { base: 6, md: '128px' }}
                    py={config.image ? undefined : { base: 8, md: '54px' }}
                    textAlign={config.image ? 'left' : 'center'}
                    width={config.image ? undefined : '100%'}
                  >
                    <Flex color='white' direction='column' gap='18px'>
                      <Text
                        as='p'
                        fontSize='0.9375rem'
                        fontStyle='italic'
                        fontWeight='normal'
                        lineHeight='1.5'
                        maxW={config.image ? undefined : { md: '680px' }}
                      >
                        {t(`${tPrefix}.quote.text.pre`)}
                        <Text as='strong' fontStyle='italic' fontWeight='700'>
                          {t(`${tPrefix}.quote.text.emphasis`)}
                        </Text>
                        {t(`${tPrefix}.quote.text.post`)}
                      </Text>
                      <Flex align={config.image ? 'flex-start' : 'center'} direction='column' gap='2px'>
                        <Text
                          as='strong'
                          color='white'
                          display='block'
                          fontSize='1rem'
                          fontStyle='normal'
                          fontWeight='700'
                          lineHeight='1.5'
                        >
                          {t(`${tPrefix}.quote.attribution.name`)}
                        </Text>
                        <Text
                          as='cite'
                          color='whiteAlpha.800'
                          display='block'
                          fontSize='0.875rem'
                          fontStyle='normal'
                          fontWeight='normal'
                          lineHeight='1.5'
                        >
                          {t(`${tPrefix}.quote.attribution.role`)}
                        </Text>
                      </Flex>
                    </Flex>
                    {config.cta && (
                      <Button
                        as='a'
                        borderWidth='2px'
                        color='white'
                        colorScheme='white'
                        fontSize='15px'
                        fontWeight='500'
                        href={config.cta.href}
                        leftIcon={<BriefcaseIcon height='20px' width='20px' />}
                        variant='outline'
                        width={{ base: '100%', md: 'fit-content' }}
                      >
                        {t(`${tPrefix}.cta.label`)}
                      </Button>
                    )}
                  </Flex>
                )}

                {!showQuotePanel && config.image && (
                  <Flex
                    alignItems='center'
                    display={{ base: 'none', md: 'flex' }}
                    flex={{ md: '1 1 490px' }}
                    justifyContent='center'
                  >
                    <Box borderRadius='8px' height='100%' overflow='hidden' position='relative' width='100%'>
                      {renderTestimonialImage(config.image.alt ?? '', '(max-width: 768px) 100vw, 621px')}
                    </Box>
                  </Flex>
                )}
              </Flex>
            </Box>

            {showQuotePanel && (
              <Flex
                align='center'
                as='blockquote'
                bg='#f4f8ff'
                border='1px solid'
                borderColor='rgba(123, 162, 255, 0.2)'
                borderRadius='12px'
                direction='column'
                gap='15px'
                justify='center'
                px={{ base: 6, md: '128px' }}
                py={{ base: 8, md: '54px' }}
                textAlign='center'
              >
                <Text
                  as='p'
                  color='grey.900'
                  fontSize={{ base: '1.5rem', md: '2.25rem' }}
                  fontStyle='italic'
                  fontWeight='600'
                  lineHeight='normal'
                >
                  {t(`${tPrefix}.quote.text`)}
                </Text>
                <Text as='cite' color='black' fontSize='0.9375rem' fontStyle='normal' lineHeight='1.5'>
                  {t(`${tPrefix}.quote.attribution`)}
                </Text>
              </Flex>
            )}
          </Flex>
        </ContentContainer>
      </Box>
      {config.video && (
        <VideoModal
          title={t(`${tPrefix}.videoModalTitle`) as string}
          videoId={isOpen ? config.video.videoId : null}
          onClose={onClose}
        />
      )}
    </>
  );
}
